import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Check, X, Loader2, Send } from 'lucide-react';
import axios from 'axios';
import { API, Logo } from '../../constants';

// Contact Section
export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: 'Správa bola odoslaná! Čoskoro vás kontaktujeme.' });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: 'Nepodarilo sa odoslať správu. Skúste to znova.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Contact Info */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00BFFF] mb-4">
              Kontakt
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium mb-8">
              Napíšte nám
            </h2>
            <p className="text-zinc-400 mb-12">
              Máte otázky alebo chcete nezáväznú konzultáciu? Kontaktujte nás!
            </p>

            <div className="space-y-6">
              <a 
                href="tel:+421915264064"
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-lg bg-[#00BFFF]/10 flex items-center justify-center group-hover:bg-[#00BFFF]/20 transition-colors">
                  <Phone className="w-5 h-5 text-[#00BFFF]" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Telefón</p>
                  <p className="font-medium">+421 915 264 064</p>
                </div>
              </a>

              <a 
                href="mailto:m.petor@seznam.cz"
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-lg bg-[#00BFFF]/10 flex items-center justify-center group-hover:bg-[#00BFFF]/20 transition-colors">
                  <Mail className="w-5 h-5 text-[#00BFFF]" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Email</p>
                  <p className="font-medium">m.petor@seznam.cz</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#00BFFF]/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#00BFFF]" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Adresa</p>
                  <p className="font-medium">Šípková 5, 900 27 Bernolákovo</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#00BFFF]/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#00BFFF]" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Otváracie hodiny</p>
                  <p className="font-medium">Po - Pia: 8:00 - 17:00</p>
                  <p className="font-medium">Sobota: 8:00 - 13:00</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-12 rounded-xl overflow-hidden border border-white/10">
              <iframe
                data-testid="google-map"
                title="Mapa"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2662.0!2d17.3!3d48.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDEyJzAwLjAiTiAxN8KwMTgnMDAuMCJF!5e0!3m2!1sen!2ssk!4v1"
                width="100%"
                height="250"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) grayscale(50%)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-medium mb-8">Odoslať správu</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="form-label">Meno *</label>
                <input
                  type="text"
                  data-testid="contact-form-name"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Vaše meno"
                />
              </div>

              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  data-testid="contact-form-email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="vas@email.sk"
                />
              </div>

              <div>
                <label className="form-label">Telefón</label>
                <input
                  type="tel"
                  data-testid="contact-form-phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+421 9XX XXX XXX"
                />
              </div>

              <div>
                <label className="form-label">Správa *</label>
                <textarea
                  data-testid="contact-form-message"
                  className="form-input min-h-[150px] resize-none"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  placeholder="Opíšte vašu požiadavku..."
                />
              </div>

              {status.message && (
                <div 
                  data-testid="contact-form-status"
                  className={`p-4 rounded-lg ${
                    status.type === 'success' 
                      ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {status.type === 'success' ? <Check size={18} /> : <X size={18} />}
                    {status.message}
                  </div>
                </div>
              )}

              <button
                type="submit"
                data-testid="contact-form-submit"
                disabled={loading}
                className="w-full btn-primary btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Odoslať správu
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
export const Footer = () => {
  return (
    <footer data-testid="footer" className="border-t border-white/10 py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <Logo />
          
          <div className="flex items-center gap-6 text-zinc-400 text-sm">
            <span>&copy; 2026 JasneSvetla.sk</span>
            <span>&bull;</span>
            <span>Všetky práva vyhradené</span>
          </div>

          <div className="flex items-center gap-4 text-zinc-500 text-sm">
            <a href="tel:+421915264064" className="hover:text-[#00BFFF] transition-colors">
              +421 915 264 064
            </a>
            <span>&bull;</span>
            <Link to="/blog" className="hover:text-[#00BFFF] transition-colors">
              Blog
            </Link>
            <span>&bull;</span>
            <Link to="/login" className="hover:text-[#00BFFF] transition-colors">
              Admin
            </Link>
            <span>&bull;</span>
            <Link to="/ochrana-osobnych-udajov" className="hover:text-[#00BFFF] transition-colors">
              Ochrana údajov
            </Link>
          </div>
        </div>
        {/* SEO backlinks */}
        <div className="mt-4 flex flex-wrap gap-4 justify-center text-xs text-[#05050A]">
          <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné dopyt</a>
          <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné služby</a>
          <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Rekonštrukcie</a>
          <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné firmy</a>
          <a href="https://www.stavebne-dopyty.sk" target="_blank" rel="noopener noreferrer">Stavebné projekty</a>
        </div>
      </div>
    </footer>
  );
};
